import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DatePicker from '../components/DatePicker';
import { api } from '../api/client';
import { invalidateSafety } from '../lib/safety-invalidation';
import { formatTimeAgo, todayCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { feedTime, orderSafetyFeed, SafetyFeedEntry } from '../lib/safety-feed';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  ClipboardCheck,
  ClipboardList,
  Wrench,
  Loader2,
  X,
  Plus,
  Award,
  ChevronRight,
  ChevronDown,
  Circle,
  ShieldCheck,
  Activity,
  FileText,
  FolderOpen,
} from 'lucide-react';
import { useHideAgentFab } from '../components/AgentFab';
import { useCollapsibleGroups } from '../hooks/useCollapsibleGroups';
import { compliancePct, readCompliance, type ComplianceBar } from '../lib/safety-compliance';
import { safetyDashboardQuery, teamCertSummaryQuery } from '../pages/safety/safety-queries';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
  type LoadFailureKind,
} from '../lib/load-failure';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { useAuth } from '../contexts/AuthContext';
import { useTenantFormTiles } from '../hooks/useTenantFormTiles';
import { NewFormPickerDialog } from '../pages/safety/NewSafetyFormButton';

/* StatTile moved out of this screen when the band arrived: the four figures it
 * used to draw are the band's stat strip now, and the tile component still
 * lives in components/ui/StatTile for the Safety Metrics page. */

/* The band and the section strip that used to be drawn here belong to the
 * whole /safety family now, and live in `pages/safety/SafetyLayout.tsx`. This
 * screen is the Overview tab under it, and owns neither the `<h1>` nor the band. */

// ─── Panel ───────────────────────────────────────────────────────────────────
/**
 * The register panel idiom: one panel, one amber 4px left edge on the PANEL,
 * an eyebrow + `<h2>` header, and a footer only where it carries something the
 * reader could act on — a count, a scope, a permission or a timestamp. A footer
 * describing the app's own internals is not one (fe#711).
 */
/**
 * ── A COLLAPSIBLE PANEL: THE TOGGLE IS THE CHEVRON, NEVER THE HEADER (fe#716) ──
 *
 * The Alerts idiom, applied to a panel rather than a table group. The toggle is
 * a `<button>` INSIDE the header, wrapping the chevron, the title and the
 * summary; the `<header>` itself keeps no `tabIndex`, no click handler, no role
 * and no tint. `aria-expanded` on the button, `aria-controls` at the body,
 * which stays in the DOM (`hidden`) so the relationship survives collapse.
 *
 * THE SUMMARY IS RENDERED IN BOTH STATES AND IS IN THE BUTTON'S NAME. A
 * collapsed panel is honest only if the size of what it holds is legible
 * without opening it — to a screen reader too — so `summaryLabel` goes into the
 * accessible name and the visible summary is `aria-hidden`, or the figures
 * would be spoken twice. The figures are the API's, as the body shows them;
 * nothing is summed or recomputed for the summary.
 */
type PanelCollapse = {
  id: string;
  collapsed: boolean;
  onToggle: () => void;
  /** Visible summary beside the title, shown open and shut. */
  summary: React.ReactNode;
  /** The same summary as words, for the button's accessible name. */
  summaryLabel: string;
};

function Panel({
  eyebrow,
  title,
  icon,
  action,
  footer,
  collapse,
  children,
}: {
  eyebrow: string;
  title: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  footer?: React.ReactNode;
  collapse?: PanelCollapse;
  children: React.ReactNode;
}) {
  const bodyId = collapse ? `${collapse.id}-body` : undefined;
  const shut = collapse?.collapsed ?? false;
  return (
    <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      <header className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
          {collapse ? (
            <h2 className="mt-0.5 text-sm font-bold text-fieldiq-text">
              <button
                type="button"
                onClick={collapse.onToggle}
                aria-expanded={!shut}
                aria-controls={bodyId}
                aria-label={`${title}, ${collapse.summaryLabel}`}
                className="-mx-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-fi-pill px-1.5 py-0.5 text-left outline-none transition hover:bg-fieldiq-dark/70 focus-visible:bg-fi-amber/10"
              >
                <span className="shrink-0 text-fieldiq-muted" aria-hidden="true">
                  {shut ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
                </span>
                <span className="flex items-center gap-2" aria-hidden="true">
                  {icon}
                  {title}
                </span>
                <span aria-hidden="true" className="text-xs font-semibold tabular-nums text-fieldiq-muted">
                  {collapse.summary}
                </span>
              </button>
            </h2>
          ) : (
            <h2 className="mt-0.5 flex items-center gap-2 text-sm font-bold text-fieldiq-text">
              {icon}
              {title}
            </h2>
          )}
        </div>
        {action}
      </header>
      <div id={bodyId} hidden={shut}>
        <div className="border-t border-fieldiq-border">{children}</div>
        {footer && (
          <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">{footer}</p>
        )}
      </div>
    </section>
  );
}

/** The plain "there are none" line, worded as a fact about the data. */
function EmptyLine({ children }: { children: React.ReactNode }) {
  return <p className="px-4 py-6 text-center text-sm text-fieldiq-muted">{children}</p>;
}

// ─── Status badges ───────────────────────────────────────────────────────────
/**
 * SATURATED FILL, DARK INK, AND EXHAUSTIVE OVER WHAT THE SERVER CAN SEND.
 *
 * `safety_incidents.status` is a `varchar(50)` with a `'reported'` default and
 * no database enum, so this is a free-text column in the sense CLAUDE.md means:
 * an unmapped value gets a slate pill reading `—`, never a guessed label.
 *
 * THE OLD PILL WAS A BINARY CHECK AND IT WAS WRONG ON PRODUCTION DATA. It read
 * `nm.status === 'open' ? amber : green` — so every other value rendered green,
 * the colour that means resolved. `reported` IS a real value (four of Smith's
 * incidents carry it today) and the API's own definition of open is
 * `status != 'closed'`, so a near miss nobody had looked at was painted as
 * closed. That is the label-map rule in the "Known Patterns" section: exhaustive
 * maps, never binary.
 *
 * Only `closed` is green. Everything else the server actually sends is work
 * still outstanding, and shares amber — the label separates them, which is the
 * documented answer when an enum has more values than the palette has hues.
 */
const NEAR_MISS_STATUS: Record<string, { label: string; fill: string }> = {
  reported: { label: 'Reported', fill: 'bg-fi-signal-amber' },
  open: { label: 'Open', fill: 'bg-fi-signal-amber' },
  investigating: { label: 'Investigating', fill: 'bg-fi-signal-amber' },
  closed: { label: 'Closed', fill: 'bg-fi-signal-green' },
};

/**
 * Four severities, three meaningful hues. `high` and `critical` share red
 * because both mean "this one could have hurt somebody badly" and the badge
 * text separates them; blue is not on this ladder at all, since it means
 * committed-to-a-job elsewhere in the app and would read as a fourth rung that
 * is not one. A hue added for this page alone is a hue with no light-mode or
 * contrast review behind it.
 */
const NEAR_MISS_SEVERITY: Record<string, { label: string; fill: string }> = {
  low: { label: 'Low', fill: 'bg-fi-signal-green' },
  medium: { label: 'Medium', fill: 'bg-fi-signal-amber' },
  high: { label: 'High', fill: 'bg-fi-signal-red' },
  critical: { label: 'Critical', fill: 'bg-fi-signal-red' },
};

/**
 * `pending` is NOT in this map, and that is the fix rather than an omission.
 * The old code read `ins.overall_result || 'pending'`, which MINTS a status for
 * a row that has none — a confident claim about an inspection the screen could
 * not classify. An absent result is a fact about the data and renders as a dash.
 */
const INSPECTION_RESULT: Record<string, { label: string; fill: string }> = {
  pass: { label: 'Pass', fill: 'bg-fi-signal-green' },
  fail: { label: 'Fail', fill: 'bg-fi-signal-red' },
  conditional: { label: 'Conditional', fill: 'bg-fi-signal-amber' },
};

const UNKNOWN_BADGE = { label: '—', fill: 'bg-fi-signal-slate' };

function Badge({ entry }: { entry: { label: string; fill: string } }) {
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-fi-signal-ink ${entry.fill}`}
    >
      {entry.label}
    </span>
  );
}

// ─── Compliance Progress Bar ─────────────────────────────────────────────────
/**
 * One coverage bar. `bar` comes from `readCompliance`, so both counts are real
 * numbers by the time they arrive here — an unreadable payload is an error
 * state one level up, never a bar.
 *
 * An empty DENOMINATOR still reaches this component and is not an error: a
 * tenant with no jobs in the 30-day window has nothing to be compliant about.
 * That renders as a bare em dash over an empty track. Not `0/0`, not `0%`, and
 * above all not red — see the header of `lib/safety-compliance.ts`.
 *
 * The dash carries no explanatory subtext on purpose. Four bars share this
 * component and they do not share a denominator: the two job bars count jobs,
 * the two equipment bars count EQUIPMENT. Any sentence naming what is missing
 * would be wrong on half of them, and a wrong caption is worse than none.
 */
function ProgressBar({ label, bar }: { label: string; bar: ComplianceBar }) {
  const pct = compliancePct(bar);

  if (pct === null) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm text-white">{label}</span>
          <span className="text-sm font-bold text-fieldiq-muted">—</span>
        </div>
        {/* Empty track, no fill. A zero-width red bar still reads as a failure. */}
        <div className="h-2 bg-fieldiq-dark rounded-full overflow-hidden" />
      </div>
    );
  }

  const color = pct >= 90 ? 'bg-green-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = pct >= 90 ? 'text-green-400' : pct >= 60 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-white">{label}</span>
        <div className="text-right">
          <span className={`text-sm font-bold ${textColor}`}>{bar.completed}/{bar.total}</span>
          <span className="text-xs text-fieldiq-muted ml-1">{pct}%</span>
        </div>
      </div>
      <div className="h-2 bg-fieldiq-dark rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Near Miss Report Modal ──────────────────────────────────────────────────
function NearMissModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const timeZone = useTenantTimeZone();
  // The TENANT's today; '' (an empty picker) when the zone is unknown — an input seed only.
  const [date, setDate] = useState(todayCalendarDate(timeZone) ?? '');
  const [severity, setSeverity] = useState('low');
  const [immediateAction, setImmediateAction] = useState('');
  const [preventiveAction, setPreventiveAction] = useState('');

  const mutation = useMutation({
    mutationFn: (data: any) => api.safety.createNearMiss(data),
    onSuccess: () => { onSuccess(); onClose(); },
  });

  /* The nav clearance rides on the card as a margin, not on the scroller as padding:
   * WebKit leaves a scroll container's bottom padding out of the scrollable overflow
   * region, so pb- here scrolled away and the Submit button stayed under the nav.
   * A flex item's bottom margin is honoured. */
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 pt-16 overflow-y-auto" onClick={onClose}>
      <div className="bg-fieldiq-card rounded-xl p-5 w-full max-w-lg space-y-4 mb-[var(--bottom-chrome)]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white text-lg">Report Near Miss</h3>
          <button onClick={onClose} className="text-fieldiq-muted"><X size={18} /></button>
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
            placeholder="Brief description of near miss" />
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Description *</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
            placeholder="What happened? What could have happened?" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-fieldiq-text mb-1 block">Date</label>
            <DatePicker value={date} onChange={setDate} />
          </div>
          <div>
            <label className="text-xs text-fieldiq-text mb-1 block">Severity</label>
            <select value={severity} onChange={e => setSeverity(e.target.value)}
              className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Location</label>
          <input value={location} onChange={e => setLocation(e.target.value)}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
            placeholder="Where did this occur?" />
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Immediate Action Taken</label>
          <textarea value={immediateAction} onChange={e => setImmediateAction(e.target.value)} rows={2}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
            placeholder="What was done right away?" />
        </div>

        <div>
          <label className="text-xs text-fieldiq-text mb-1 block">Preventive Action Recommended</label>
          <textarea value={preventiveAction} onChange={e => setPreventiveAction(e.target.value)} rows={2}
            className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
            placeholder="What should be done to prevent recurrence?" />
        </div>

        {mutation.isError && (
          <p className="text-red-400 text-xs">{(mutation.error as any)?.response?.data?.error || 'Failed to submit'}</p>
        )}

        <button
          onClick={() => mutation.mutate({ title, description, location, date, severity, immediateAction, preventiveAction })}
          disabled={!title || !description || mutation.isPending}
          className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {mutation.isPending && <Loader2 size={16} className="animate-spin" />}
          Submit Near Miss Report
        </button>
      </div>
    </div>
  );
}

// ─── Main Screen ─────────────────────────────────────────────────────────────
export default function SafetyScreen({ basePath = '/safety' }: { basePath?: string }) {
  useHideAgentFab();  // Near Miss modal's Submit sits where the FAB floats.
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();
  const { hasVertical } = useAuth();
  const casingTenant = hasVertical('casing_running');
  const [showNearMissModal, setShowNearMissModal] = useState(false);
  /* The tenant's own custom forms after the five built-ins, opening the fill
   * screen; past four, "More forms" opens the same picker as the Custom Forms
   * tab's "New form" button, so the two entry points list the same forms. */
  const formTiles = useTenantFormTiles();
  const [showFormPicker, setShowFormPicker] = useState(false);

  /* `?report=near-miss` opens the modal directly. Report Incident links here
   * after its Near Miss option was removed, so the signpost has to land on the
   * open form rather than on a screen where the reader has to find it again.
   * The param is consumed on arrival so a refresh does not reopen the modal. */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('report') !== 'near-miss') return;
    setShowNearMissModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('report');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /* ── EVERY LIST QUERY CARRIES A FAILURE BRANCH NOW ───────────────────────
   *
   * Eight of the nine reads on this screen had two branches by construction:
   * `{ data }`, then `?? []`. A 403 or a dead zone arrived as `undefined`, the
   * coalesce turned it into an empty list, and the panel said "No near miss
   * reports yet" / "No recent safety activity" / "No submitted inspections
   * yet". Those are the reassuring sentences, on the screen whose entire job is
   * to report that something is wrong — the AlertsScreen shape (fe#704, fe#697,
   * fe#573), here in eight copies.
   *
   * `LOAD_FAILURE_QUERY_OPTIONS` matters as much as the branch. React Query's
   * default `networkMode: 'online'` sends a retrying 5xx to PAUSED, where
   * `error` is null, `data` is undefined and `isLoading` is FALSE — so a screen
   * branching on `error` renders the empty state anyway, and a screen verified
   * against a 403 alone looks completely correct while being unfixed for every
   * other failure. `queryLoadFailure` reads the paused state too.
   */
  /* The band's three reads are shared with `SafetyLayout` — one definition in
   * `safety-queries.ts`, so the band's figure and this panel's figure are the
   * same cache entry and cannot disagree. */
  const dashboardQuery = useQuery(safetyDashboardQuery);

  const jsaQuery = useQuery({
    queryKey: ['safety-jsa-list'],
    queryFn: () => api.safety.jsaList().then(r => r.data.data ?? r.data ?? []),
    refetchOnMount: 'always',
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const meetingsQuery = useQuery({
    queryKey: ['safety-meetings'],
    queryFn: () => api.safety.meetings().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const incidentsQuery = useQuery({
    queryKey: ['safety-incidents'],
    queryFn: () => api.safety.incidents().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  /* `readCompliance` returns null for every payload we cannot read, and axios
   * rejects the API's 500 before the `.then` runs. The two arrive here as
   * separate signals and are collapsed into one below — see `complianceFailed`. */
  const complianceQuery = useQuery({
    queryKey: ['safety-compliance'],
    queryFn: () => api.safety.compliance().then(r => readCompliance(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const teamCertsQuery = useQuery(teamCertSummaryQuery);

  const nearMissQuery = useQuery({
    queryKey: ['safety-near-misses'],
    queryFn: () => api.safety.nearMisses().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  // Casing only: /casing/* refuses every other tenant, and its panel below is
  // not drawn for them (inspections PR 2).
  const casingInspectionsQuery = useQuery({
    queryKey: ['casing-inspection-submissions'],
    queryFn: () => api.casingInspections.list({ status: 'submitted' }).then(r => r.data.data ?? r.data ?? []),
    enabled: casingTenant,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });


  const dashboardFailure = queryLoadFailure(dashboardQuery);
  const jsaFailure = queryLoadFailure(jsaQuery);
  const meetingsFailure = queryLoadFailure(meetingsQuery);
  const incidentsFailure = queryLoadFailure(incidentsQuery);
  const teamCertsFailure = queryLoadFailure(teamCertsQuery);
  const nearMissFailure = queryLoadFailure(nearMissQuery);
  const inspectionsFailure = queryLoadFailure(casingInspectionsQuery);

  const dashboard = dashboardQuery.data;
  const teamCerts = teamCertsQuery.data;

  /* HAS IT ARRIVED? `data !== undefined` rather than a length or a truthiness
   * check — an empty answer is a real answer and has to stay distinguishable
   * from no answer. `isLoading` alone is not the question: a paused query has
   * `isLoading === false` over `undefined` data, which is how a band of zeros
   * gets rendered above a panel saying the read failed (fe#729). */
  const dashboardArrived = !dashboardQuery.isLoading && !dashboardFailure && dashboard !== undefined;
  const teamCertsArrived = !teamCertsQuery.isLoading && !teamCertsFailure && teamCerts !== undefined;

  const jsas = Array.isArray(jsaQuery.data) ? jsaQuery.data : [];
  const jsaPending = dashboard?.pendingJsaApprovals;

  /* One fact, two signals. A 500 rejects and sets the query's error; a 200
   * carrying an envelope, an HTML edge-fallback body or a missing bar comes
   * back as null from `readCompliance`. Both say "we could not read this",
   * which is NOT the same as "there is nothing to read".
   *
   * THE REFUSED/UNREACHABLE SPLIT NOW SURVIVES, and it did not before: this
   * used to render one sentence ending "Please try again", which is useless
   * advice to a dispatcher the API will refuse every time. A malformed 200 has
   * no error object to classify and is reported as unreachable — the server
   * answered with something this screen cannot read, and retrying is the only
   * move left. */
  const complianceLoadFailure = queryLoadFailure(complianceQuery);
  const compliance = complianceQuery.data;
  const complianceUnreadable =
    !complianceLoadFailure && !complianceQuery.isLoading && compliance === null;
  const complianceFailureKind: LoadFailureKind | null =
    complianceLoadFailure ?? (complianceUnreadable ? 'unreachable' : null);

  /* Same figure as the "Jobs with JSA" bar below, so it carries the same
   * noun: COUNT(DISTINCT jsa.job_id) with an approved JSA, not a count of
   * JSAs. Undefined, not `?? 0`: "0 jobs with JSA" from a failed fetch is the
   * same lie the bars used to tell, in a sentence. */
  const jobsWithJsa = compliance?.jsas.completed;
  const meetings = Array.isArray(meetingsQuery.data) ? meetingsQuery.data : [];
  const nearMisses: any[] = Array.isArray(nearMissQuery.data) ? nearMissQuery.data : [];
  const casingInspections: any[] = Array.isArray(casingInspectionsQuery.data) ? casingInspectionsQuery.data : [];

  /* Incidents only, as the API sends them. Near misses share the table, and
   * GET /safety/incidents has excluded them since api#246 — the same rule the
   * dashboard's aggregates and Records' Incidents tab read. This screen used to
   * filter them out again, a second definition of "incident" on the client;
   * it is gone so there is one answer to the question, and it is the API's. */
  const incidents: any[] = Array.isArray(incidentsQuery.data) ? incidentsQuery.data : [];

  /* Straight from the API. These used to be derived here because the server's
   * figures counted near misses; they no longer do. Deriving a second answer
   * from the incident list would leave two sources of truth for one number,
   * and nothing to say which is right on the day they disagree. */
  const openNearMissCount = dashboard?.openNearMisses;

  /* "Since Last Incident" and its three states moved to `SafetyLayout` with
   * the band. */

  /* THE BAND'S FOURTH FIGURE WAS THE SUM OF TWO DIFFERENT JOBS. It read
   * `expiringSoon + expired` under the word "Certs Expiring" — 4 + 43 = 47 on
   * Smith — directly above a panel reading 4 expiring and 43 expired. The API
   * returns three disjoint buckets and the panel was right.
   *
   * The band now names the one that stops work: an EXPIRED certification means
   * that person cannot be sent out today. Expiring-soon is a planning number
   * and keeps its own row in the panel below, where it can be read as the
   * different thing it is. The stat's link already pointed at
   * `?status=expired`, so the label was the only half that was wrong. */
  const certsExpired = teamCerts?.expired;
  const certsExpiringSoon = teamCerts?.expiringSoon;
  const certsCurrent = teamCerts?.current;

  // Recent activity — collect with raw timestamp, then sort newest-first
  const recentActivity: SafetyFeedEntry[] = [];
  /**
   * The `||` fallback arms hand a CALENDAR DAY to instant readers, and they do
   * it twice per entry — once for the label and once for `_ts`, which sorts the
   * feed. `new Date('2026-08-19')` is UTC midnight, six or seven hours behind
   * local midnight, so a day-only entry both announced the wrong age AND sorted
   * below same-day instants that were actually older. `feedTime` picks the right
   * reader for whichever value survived.
   */
  meetings.slice(0, 3).forEach((m: any) => {
    const t = feedTime(m.createdAt, m.meetingDate, timeZone);
    recentActivity.push({ type: 'success', text: `Pre-job meeting completed${m.jobNumber ? ` - ${m.jobNumber}` : ''}`, time: t.label, ts: t.ts, kind: 'routine' });
  });
  jsas.slice(0, 3).forEach((j: any) => {
    if (j.status === 'approved') {
      const raw = j.updatedAt || j.createdAt;
      recentActivity.push({ type: 'success', text: `JSA approved${j.jobNumber ? ` - ${j.jobNumber}` : ''}`, time: formatTimeAgo(raw, timeZone), ts: new Date(raw || 0).getTime(), kind: 'routine' });
    } else if (j.status === 'submitted') {
      const raw = j.createdAt;
      recentActivity.push({ type: 'warning', text: `JSA awaiting approval${j.jobNumber ? ` - ${j.jobNumber}` : ''}`, time: formatTimeAgo(raw, timeZone), ts: new Date(raw || 0).getTime(), kind: 'routine' });
    }
  });
  incidents.slice(0, 2).forEach((inc: any) => {
    const t = feedTime(inc.createdAt, inc.incidentDate, timeZone);
    recentActivity.push({ type: 'danger', text: `Incident reported${inc.incidentNumber ? ` - ${inc.incidentNumber}` : ''}`, time: t.label, ts: t.ts, kind: 'incident' });
  });
  /**
   * Was an inline `sort((a, b) => b._ts - a._ts)`. Right shape, one key short:
   * this screen cuts to six over ≤3 meetings + ≤3 JSAs + ≤2 incidents, so six
   * newer routine rows displace an incident on recency alone. `orderSafetyFeed`
   * reserves incidents ahead of routine items and is shared with the field
   * screen, which had no sort whatever.
   */
  const displayActivity = orderSafetyFeed(recentActivity, 6);

  /* Both halves of the summary line, from two different calls. `isLoading` is
   * the right question HERE and not in `dashboardArrived` above: this asks
   * "has the request stopped being in flight", and a paused query has already
   * failed, so its dash is a real absence rather than a placeholder. */
  const summaryArrived = dashboardArrived && !complianceQuery.isLoading;

  /**
   * THE FEED HAS THREE SOURCES AND A PARTIAL ONE MUST NOT READ AS THE WHOLE.
   *
   * Meetings, JSAs and incidents are merged here, so any one of them failing
   * leaves a feed that is short by an unknown amount — and on this page the
   * documented failure is UNDER-reporting. The rows that did arrive are still
   * worth showing, so they are shown; the notice goes underneath them rather
   * than in place of them, and says which kind of not-arriving it was. When
   * nothing arrived at all it stands alone where the empty state would have
   * been. A refusal outranks an unreachable server in the wording, because
   * "ask an admin" is the actionable half when both are true.
   */
  /**
   * ── TWO PANELS COLLAPSE, AND BOTH START OPEN ────────────────────────────────
   *
   * Team Certification Status and Compliance Tracking are the two panels a
   * reader may want out of the way while working further down. They default
   * OPEN, for Alerts' reason (fe#716): this page's documented failure is
   * UNDER-reporting, and nobody arrives here knowing which panel to skip. A
   * threshold ("collapse when …") is the rejected shape — it would render
   * differently per tenant and per day, and hide a panel on the app's own
   * initiative. Collapse all / Expand all is the one-click escape, remembered
   * for this tab's session and never stamped on mount (`useCollapsibleGroups`).
   *
   * Quick Actions and Recent Activity do not collapse. Quick Actions is the
   * five ways to START a record (plus the tenant's own custom forms) — hiding "Report Incident" behind a chevron is
   * the wrong trade on a safety page. Recent Activity is capped at six rows, so
   * there is nothing to get out of the way.
   */
  const { isCollapsed, toggleGroup, expandAll, collapseAll } = useCollapsibleGroups({
    defaultCollapsed: false,
    storageKey: 'safety:panels:collapsed',
  });
  const COLLAPSIBLE_PANELS = ['safety-team-certs', 'safety-compliance'];
  const allPanelsCollapsed = COLLAPSIBLE_PANELS.every((id) => isCollapsed(id));

  /* The collapsed summaries: the same figures each body shows, never a sum.
   * Three disjoint cert buckets stay three (fe#757 is the sum that shipped);
   * an absent figure is an em dash in both the visible line and the name. */
  const certSummaryParts: Array<[string, unknown]> = [
    ['expired', certsExpired],
    ['expiring', certsExpiringSoon],
    ['current', certsCurrent],
  ];
  const certSummary = certSummaryParts.map(([k, v]) => `${displayValue(v as any)} ${k}`).join(' · ');

  const complianceParts: Array<[string, string, ComplianceBar | undefined]> = compliance
    ? [
        ['Meetings', 'jobs with a pre-job meeting', compliance.meetings],
        ['JSA', 'jobs with a JSA', compliance.jsas],
        ['Inspections', 'equipment inspected', compliance.inspections],
        ...(compliance.equipmentChecks
          ? [['Pre-use', 'equipment pre-use checked', compliance.equipmentChecks] as [string, string, ComplianceBar]]
          : []),
      ]
    : [];
  const complianceSummary = complianceParts.length
    ? complianceParts.map(([short, , b]) => `${short} ${b!.completed}/${b!.total}`).join(' · ')
    : '\u2014';
  const complianceSummaryLabel = complianceParts.length
    ? complianceParts.map(([, long, b]) => `${long} ${b!.completed} of ${b!.total}`).join(', ')
    : 'figures not loaded';

  const feedFailure: LoadFailureKind | null =
    [meetingsFailure, jsaFailure, incidentsFailure].find(f => f === 'refused') ??
    [meetingsFailure, jsaFailure, incidentsFailure].find(f => f) ??
    null;

  return (
    <>
      {/* ── Quick Actions ──
        * The five creation entry points, first in the body and directly
        * under the strip. They are what a hand or a supervisor came here to
        * do, and every one of them writes a record the rest of this page
        * then reports on. */}
      <Panel eyebrow="Start a record" title="Quick Actions" icon={<Plus size={14} className="text-fieldiq-accent" />}>
        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-5">
          <button onClick={() => navigate(`${basePath}/incident`)}
            className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-dark p-5 text-center transition-colors hover:border-fi-amber">
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-red-500/15">
              <AlertTriangle size={22} className="text-red-400" />
            </div>
            <span className="text-xs font-medium leading-tight text-fieldiq-text">Report Incident</span>
          </button>
          <button onClick={() => navigate(`${basePath}/meeting`)}
            className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-dark p-5 text-center transition-colors hover:border-fi-amber">
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-green-500/15">
              <ClipboardCheck size={22} className="text-green-400" />
            </div>
            <span className="text-xs font-medium leading-tight text-fieldiq-text">Pre-Job Meeting</span>
          </button>
          <button onClick={() => navigate(`${basePath}/inspection`)}
            className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-dark p-5 text-center transition-colors hover:border-fi-amber">
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-blue-500/15">
              <Wrench size={22} className="text-blue-400" />
            </div>
            <span className="text-xs font-medium leading-tight text-fieldiq-text">Equipment Inspection</span>
          </button>
          <button onClick={() => navigate(`${basePath}/jsa`)}
            className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-dark p-5 text-center transition-colors hover:border-fi-amber">
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-amber-500/15">
              <ClipboardList size={22} className="text-amber-400" />
            </div>
            <span className="text-xs font-medium leading-tight text-fieldiq-text">Job Safety Analysis</span>
          </button>
          <button onClick={() => setShowNearMissModal(true)}
            className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-dark p-5 text-center transition-colors hover:border-fi-amber">
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-purple-500/15">
              <Circle size={22} className="text-purple-400" />
            </div>
            <span className="text-xs font-medium leading-tight text-fieldiq-text">Near Miss Report</span>
          </button>
          {formTiles.tiles.map(tile => (
            <button key={tile.id} onClick={() => navigate(tile.route)}
              className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-dark p-5 text-center transition-colors hover:border-fi-amber">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-fieldiq-accent/15">
                <FileText size={22} className="text-fieldiq-accent" />
              </div>
              <span className="text-xs font-medium leading-tight text-fieldiq-text">{tile.name}</span>
            </button>
          ))}
          {formTiles.hasMore && (
            <button onClick={() => setShowFormPicker(true)}
              className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-dark p-5 text-center transition-colors hover:border-fi-amber">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-fieldiq-accent/15">
                <FolderOpen size={22} className="text-fieldiq-accent" />
              </div>
              <span className="text-xs font-medium leading-tight text-fieldiq-text">More forms</span>
            </button>
          )}
        </div>
        {/* A missing row of company forms is not "this company has none". */}
        {formTiles.failure && (
          <div className="px-4 pb-4">
            <LoadFailureNotice error={queryFailureError(formTiles.failureQuery as any)} kind={formTiles.failure} subject="your company's forms" />
          </div>
        )}
      </Panel>
      <NewFormPickerDialog open={showFormPicker} onOpenChange={setShowFormPicker} />

      {/* Reports the ACTION, not the state — "Collapse all" until both are
        * shut, then "Expand all" (Alerts' reasoning). */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => (allPanelsCollapsed ? expandAll() : collapseAll())}
          className="rounded-fi-pill border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-xs font-bold text-fieldiq-text outline-none transition hover:bg-fieldiq-dark/70 focus-visible:bg-fi-amber/10"
        >
          {allPanelsCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      {/* ── Team Certification Status ──
        * Moved up from fifth to second, because the band's third figure is
        * now one of these three rows and the reader needs the other two
        * beside it. Expiring-soon lives here, un-merged. */}
      <Panel
        eyebrow="Crew readiness"
        title="Team Certification Status"
        icon={<Award size={14} className="text-fieldiq-accent" />}
        collapse={{
          id: 'safety-team-certs',
          collapsed: isCollapsed('safety-team-certs'),
          onToggle: () => toggleGroup('safety-team-certs'),
          summary: certSummary,
          summaryLabel: certSummary.replace(/ · /g, ', '),
        }}
        footer={
          teamCertsArrived
            ? 'Expired means that person cannot be sent out today. Expiring is a renewal to book.'
            : undefined
        }
      >
        {teamCertsFailure ? (
          <LoadFailureNotice
            error={queryFailureError(teamCertsQuery)}
            kind={teamCertsFailure}
            subject="certification totals"
          />
        ) : !teamCertsArrived ? (
          <div className="flex items-center justify-center px-4 py-8">
            <Loader2 className="size-5 animate-spin text-fieldiq-accent" />
          </div>
        ) : (
          <div className="divide-y divide-fieldiq-border">
            <button data-testid="cert-row-current" onClick={() => navigate('/safety/team-certs?status=current')} className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-fieldiq-dark/50">
              <span className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-400" />
                <span className="text-sm text-fieldiq-text">Current</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-sm font-bold tabular-nums text-fieldiq-text">{displayValue(certsCurrent)}</span>
                <ChevronRight size={16} className="text-fieldiq-muted" />
              </span>
            </button>
            <button data-testid="cert-row-expiring" onClick={() => navigate('/safety/team-certs?status=expiring')} className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-fieldiq-dark/50">
              <span className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-400" />
                <span className="text-sm text-fieldiq-text">Expiring (30 days)</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-sm font-bold tabular-nums text-fieldiq-text">{displayValue(certsExpiringSoon)}</span>
                <ChevronRight size={16} className="text-fieldiq-muted" />
              </span>
            </button>
            <button data-testid="cert-row-expired" onClick={() => navigate('/safety/team-certs?status=expired')} className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-fieldiq-dark/50">
              <span className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-red-400" />
                <span className="text-sm text-fieldiq-text">Expired</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="text-sm font-bold tabular-nums text-fieldiq-text">{displayValue(certsExpired)}</span>
                <ChevronRight size={16} className="text-fieldiq-muted" />
              </span>
            </button>
          </div>
        )}
      </Panel>

      {/* ── Compliance Tracking ── */}
      <Panel
        eyebrow="Last 30 days"
        title="Compliance Tracking"
        icon={<ShieldCheck size={14} className="text-fieldiq-accent" />}
        collapse={{
          id: 'safety-compliance',
          collapsed: isCollapsed('safety-compliance'),
          onToggle: () => toggleGroup('safety-compliance'),
          summary: complianceSummary,
          summaryLabel: complianceSummaryLabel,
        }}
      >
        {complianceFailureKind ? (
          /* Not an empty card and not three dashes: a dash here would claim
           * we asked and the answer was "nothing to measure". We did not get
           * an answer. */
          <LoadFailureNotice
            error={queryFailureError(complianceQuery)}
            kind={complianceFailureKind}
            subject="compliance tracking"
          />
        ) : !compliance ? (
          <div className="flex items-center justify-center px-4 py-8">
            <Loader2 className="size-5 animate-spin text-fieldiq-accent" />
          </div>
        ) : (
          <div className="divide-y divide-fieldiq-border">
            <button onClick={() => navigate('/safety/records?tab=meetings')} className="w-full transition-colors hover:bg-fieldiq-dark/50">
              {/* "Jobs with Pre-Job Meeting", not "Pre-job meetings" — same
                * reading as the JSA bar directly below, and for the same
                * reason. The API counts COUNT(DISTINCT sm.job_id) over
                * `safety_meetings` joined to the same 30-day job window the
                * JSA bar uses: two meetings on one job count once, and a
                * meeting with no job counts not at all. It is job coverage,
                * NOT a count of meetings held. */}
              <ProgressBar label="Jobs with Pre-Job Meeting" bar={compliance.meetings} />
            </button>
            <button onClick={() => navigate('/safety/records?tab=jsas')} className="w-full transition-colors hover:bg-fieldiq-dark/50">
              {/* "Jobs with JSA", not "JSAs completed" — the API measures job
                * coverage, not JSA throughput. GET /safety/compliance returns
                * total = jobs in the last 30 days (scheduled/active/completed)
                * and completed = COUNT(DISTINCT jsa.job_id) among them with an
                * approved JSA. Two approved JSAs on one job still count once,
                * and a job-less JSA counts not at all. The old label read as a
                * count of documents and made 2/9 look like seven missing
                * forms. */}
              <ProgressBar label="Jobs with JSA" bar={compliance.jsas} />
            </button>
            <button onClick={() => navigate('/safety/records?tab=inspections')} className="w-full transition-colors hover:bg-fieldiq-dark/50">
              {/* This denominator is EQUIPMENT, not jobs — which is why the
                * empty state in ProgressBar is a bare dash with no caption. */}
              <ProgressBar label="Equipment Inspections (30 days)" bar={compliance.inspections} />
            </button>
            {/* ── THE FOURTH BAR, AND IT IS NOT A SECOND SPELLING OF THE ONE
              * ABOVE. `equipment_checks` and `equipment_inspection_submissions`
              * are different tables: a CHECK is the quick pre-use pass/flag a
              * hand does on a piece of equipment, an INSPECTION is a submitted
              * inspection form. They share this denominator — the equipment
              * that should be covered, `available`/`in_use`/`left_on_site` and
              * not soft-deleted — and the API reports them as separate
              * numerators off one query. Reading either as the other overstates
              * coverage, which on a safety page is the direction that matters.
              *
              * The API has always sent it and this screen has always dropped
              * it, so a tenant doing pre-use checks and no inspection forms
              * read as 0% covered with the evidence sitting in the payload.
              * Smith reads 0/34 — a real empty numerator over 34 assets, not a
              * missing key, which is why it renders as 0/34 · 0% rather than a
              * dash.
              *
              * NOT A BUTTON, unlike the three above: `/safety/records` has six
              * tabs and none of them lists equipment checks. A row that looks
              * clickable and goes nowhere is the failure this repo keeps
              * finding, so it stays a plain row until there is somewhere for it
              * to go.
              *
              * RENDERED ONLY WHEN THE KEY IS THERE. `readCompliance` keeps it
              * optional on purpose — a bar this app did not draw must not be
              * able to blank the three it does — so an older API answering
              * without it drops this row rather than drawing a bar out of
              * nothing. An absent bar is not a 0/0 bar. */}
            {compliance.equipmentChecks && (
              <div>
                <ProgressBar label="Equipment Pre-Use Checks (30 days)" bar={compliance.equipmentChecks} />
              </div>
            )}
          </div>
        )}
      </Panel>

      {/* ── JSA summary ── */}
      <Panel eyebrow="Approvals" title="Job Safety Analyses" icon={<ClipboardList size={14} className="text-fieldiq-accent" />}>
        {dashboardFailure ? (
          <LoadFailureNotice
            error={queryFailureError(dashboardQuery)}
            kind={dashboardFailure}
            subject="safety summary figures"
          />
        ) : !summaryArrived ? (
          /* A DASH IS A PLACEHOLDER WHILE THE READ IS STILL IN FLIGHT, and
           * the em-dash rule is about a value that did not ARRIVE, not one
           * that has not arrived YET. Both figures wait together because
           * they come from two calls that settle at different moments, and
           * a line that fills in one half at a time reads as the other half
           * being absent. */
          <div className="flex items-center justify-center px-4 py-6">
            <Loader2 className="size-5 animate-spin text-fieldiq-accent" />
          </div>
        ) : (
          <button
            onClick={() => navigate('/safety/records?tab=jsas')}
            className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-fieldiq-dark/50"
          >
            {/* Both figures are absent-safe. The second is the "Jobs with
              * JSA" bar's numerator and comes from the compliance call, so
              * it goes absent when THAT call does while the first does not:
              * two sources, one line, and neither may borrow the other's
              * zero. */}
            <span data-testid="jsa-summary-line" className="text-sm text-fieldiq-text">
              <span className="font-bold tabular-nums">{displayValue(jsaPending)}</span> pending approval
              {' · '}
              <span className="font-bold tabular-nums">{displayValue(jobsWithJsa)}</span> jobs with JSA
            </span>
            <ChevronRight size={18} className="text-fieldiq-muted" />
          </button>
        )}
      </Panel>

      {/* ── Near Miss Reports ── */}
      <Panel
        eyebrow="Reported by the crew"
        title="Near Miss Reports"
        icon={<Shield size={14} className="text-fieldiq-accent" />}
        action={
          <button
            onClick={() => setShowNearMissModal(true)}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus size={14} aria-hidden="true" /> Report
          </button>
        }
        /* A scope, which is what earns a footer its place (fe#711): the
         * panel shows at most ten and the open count is the reason to open
         * the list. Absent rather than zero when the dashboard did not
         * arrive — the two figures have different sources. */
        footer={
          nearMissFailure
            ? undefined
            : nearMisses.length > 0
              ? `Showing ${Math.min(nearMisses.length, 10)} of ${nearMisses.length} · ${displayValue(openNearMissCount)} still open`
              : undefined
        }
      >
        {nearMissFailure ? (
          <LoadFailureNotice
            error={queryFailureError(nearMissQuery)}
            kind={nearMissFailure}
            subject="near miss reports"
          />
        ) : nearMisses.length === 0 ? (
          <EmptyLine>No near misses have been reported.</EmptyLine>
        ) : (
          <div className="divide-y divide-fieldiq-border">
            {nearMisses.slice(0, 10).map((nm: any) => (
              <div key={nm.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-fieldiq-text">{nm.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-fieldiq-muted">{nm.description}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {/* An unmapped severity gets a slate dash, never the
                        * nearest rung. The old code fell back to LOW, which
                        * is a confident claim that a near miss the screen
                        * could not classify was the least serious kind. */}
                      {nm.severity && <Badge entry={NEAR_MISS_SEVERITY[nm.severity] ?? UNKNOWN_BADGE} />}
                      {nm.location && <span className="text-xs text-fieldiq-muted">{nm.location}</span>}
                      <span className="text-xs text-fieldiq-muted">{formatTimeAgo(nm.created_at || nm.createdAt, timeZone)}</span>
                    </div>
                    {(nm.first_name || nm.last_name) && (
                      <p className="mt-1 text-xs text-fieldiq-text">Reported by {nm.first_name} {nm.last_name}</p>
                    )}
                  </div>
                  <Badge entry={NEAR_MISS_STATUS[nm.status] ?? UNKNOWN_BADGE} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* ── Recent Activity ── */}
      <Panel eyebrow="Newest first" title="Recent Activity" icon={<Activity size={14} className="text-fieldiq-accent" />}>
        {displayActivity.length === 0 && !feedFailure ? (
          <EmptyLine>No safety activity in this period.</EmptyLine>
        ) : (
          <>
            {displayActivity.length > 0 && (
              <div className="divide-y divide-fieldiq-border">
                {displayActivity.map((activity, index) => {
                  const color = activity.type === 'success' ? 'text-green-400' : activity.type === 'warning' ? 'text-amber-400' : 'text-red-400';
                  return (
                    <div key={index} className="flex items-start gap-3 px-4 py-3.5">
                      <span className={`mt-0.5 ${color}`}>
                        {activity.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-fieldiq-text">{activity.text}</p>
                        {activity.time && <p className="mt-0.5 text-xs text-fieldiq-muted">{activity.time}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {feedFailure && (
              <div className={displayActivity.length > 0 ? 'border-t border-fieldiq-border' : undefined}>
                <LoadFailureNotice
                  error={queryFailureError(
                    meetingsFailure ? meetingsQuery : jsaFailure ? jsaQuery : incidentsQuery
                  )}
                  kind={feedFailure}
                  subject="recent safety activity"
                />
              </div>
            )}
          </>
        )}
      </Panel>

      {/* ── Past Equipment Inspections ── casing only; see the query above. */}
      {casingTenant && (
      <Panel eyebrow="Submitted" title="Past Equipment Inspections" icon={<Wrench size={14} className="text-fieldiq-accent" />}>
        {inspectionsFailure ? (
          <LoadFailureNotice
            error={queryFailureError(casingInspectionsQuery)}
            kind={inspectionsFailure}
            subject="submitted inspections"
          />
        ) : casingInspections.length === 0 ? (
          <EmptyLine>No inspections have been submitted.</EmptyLine>
        ) : (
          <div className="divide-y divide-fieldiq-border">
            {casingInspections.slice(0, 10).map((ins: any) => {
              const result = ins.overall_result || ins.overallResult;
              return (
                <div key={ins.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-fieldiq-text">
                        {ins.form_type === 'power_tong' ? 'Power Tong Inspection' : ins.form_type || 'Equipment Inspection'}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {/* The job this inspection was done on. The list
                            endpoint has joined `jobs` since job_id was
                            added; this screen was dropping it on the floor.
                            Conditional like its siblings rather than
                            rendering an em dash — an inspection genuinely
                            need not belong to a job, and a row of dashes in
                            a chip strip is noise, not information. */}
                        {(ins.job_number || ins.jobNumber) && (
                          <span className="text-xs font-medium text-fieldiq-accent">
                            {ins.job_number || ins.jobNumber}
                          </span>
                        )}
                        {ins.general_info?.tongMake && (
                          <span className="text-xs text-fieldiq-muted">{ins.general_info.tongMake} {ins.general_info.tongModel}</span>
                        )}
                        {ins.general_info?.serialNumber && (
                          <span className="text-xs text-fieldiq-muted">S/N: {ins.general_info.serialNumber}</span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {ins.general_info?.operator && (
                          <span className="text-xs text-fieldiq-text">By {ins.general_info.operator}</span>
                        )}
                        <span className="text-xs text-fieldiq-muted">
                          {feedTime(ins.created_at || ins.createdAt, ins.general_info?.date, timeZone).label}
                        </span>
                      </div>
                    </div>
                    <Badge entry={(result && INSPECTION_RESULT[result]) || UNKNOWN_BADGE} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
      )}

      {showNearMissModal && (
        <NearMissModal
          onClose={() => setShowNearMissModal(false)}
          onSuccess={() => invalidateSafety(queryClient)}
        />
      )}
    </>
  );
}
